import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";

export interface MedicalRecord {
  id: number;
  symptoms: string;
  symptomDuration: string;
  currentMedication: string;
  preexistingCondition: string;
  familyHistory: string;
  allergies: string;
  assessment: string;
  recommendation: string;
  doctorId: number;
  patientId: number | null;
  consultationFee: number;
  amountPaid: number;
  amountDue: number;
  dateCreation: string;
  systemsReview: string | null;
  observations: string | null;
  diagnostic: string | null;
  medicalHistorySummary: string | null;
  lastAnnualCheckupDate: string | null;
  specialtyType: string | null;
  gynecologyInfo: GynecologyInfo | null;
  pediatricsInfo: PediatricsInfo | null;
  additionalExaminations: AdditionalExamination[];
  appointmentId: number | null;
  medicalHistoryId: number | null;
  constanteId: number | null;
  patientFullName?: string; // Ajouté dynamiquement via API patient
  doctorFullName?: string; // Ajouté dynamiquement via API docteur
  doctorPhone?: string; // Ajouté dynamiquement via API docteur
  phone?: string; // Ajouté dynamiquement via API patient
}

interface GynecologyInfo {
  menopauseAge: number | null;
  hysterectomyDate: string | null;
  lastSmearTestDate: string | null;
  lastMammographyDate: string | null;
  pregnancyCount: number | null;
  hadAbnormalSmear: boolean | null;
  currentContraceptiveMethod: string | null;
  pastContraceptiveMethods: string | null;
  lastMenstrualDate: string | null;
  menstrualCycleLength: number | null;
  childrenCount: number | null;
  livingChildrenCount: number | null;
  deceasedChildrenCount: number | null;
  stillbornCount: number | null;
  miscarriageCount: number | null;
  deliveryType: string | null;
}

interface PediatricsInfo {
  livingWith: string | null;
  hasPets: boolean | null;
  householdSmoking: boolean | null;
  childcareSchool: string | null;
  childSmoking: boolean | null;
  prenatalCareLocation: string | null;
  pregnancyComplications: string | null;
  tobaccoOrMedicationDuringPregnancy: string | null;
  birthLocation: string | null;
  birthWeight: number | null;
  gestationalAge: number | null;
  deliveryComplications: string | null;
  deliveryComplicationsDescription: string | null;
}

interface AdditionalExamination {
  examName: string;
  reason: string;
  concernedDiagnostics: string;
  patientAdvice: string;
}

const RecordForm: React.FC = () => {
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");

        const response = await axiosInstance.get(
          `${API_ENDPOINTS.MEDICAL_RECORDS.LIST}/search`,
          {
            params: {
              query: debouncedSearchTerm,
              page: currentPage - 1,
              size: recordsPerPage,
              sort: "dateCreation,desc",
            },
          }
        );

        let recordsData: MedicalRecord[] = [];
        let totalPagesData = 1;

        // Handle Page<MedicalRecordResponseDTO> structure
        if (response.data && Array.isArray(response.data.content)) {
          recordsData = response.data.content;
          totalPagesData = response.data.totalPages;
        } else if (Array.isArray(response.data)) {
          // Fallback for non-paginated response (should not happen with /paged)
          recordsData = response.data;
          totalPagesData = Math.ceil(response.data.length / recordsPerPage);
        } else {
          // Fallback for other structures
          recordsData = response.data.records || [];
          totalPagesData = response.data.totalPages || 1;
        }

        const recordsWithInfo = recordsData.map((record: any) => {
          // Backend now provides patientFullName, doctorFullName, and phone directly
          const patientFullName = record.patientFullName || "Patient non assigné";
          // Map patientPhone to phone for compatibility with RecordView
          const phone = record.patientPhone || record.phone || "N/A";
          const doctorFullName = record.doctorFullName || "N/A";
          const doctorPhone = record.doctorPhone || "N/A";

          // Normalisation financière côté UI (fallbacks)
          const normalized = {
            consultationFee:
              record.consultationFee ?? record.montant ?? 0,
            amountPaid:
              record.amountPaid ?? record.sommePaye ?? 0,
            amountDue:
              record.amountDue ??
              record.sommeImpaye ??
              Math.max(
                0,
                (record.consultationFee ??
                  record.montant ??
                  0) -
                (record.amountPaid ??
                  record.sommePaye ??
                  0)
              ),
          };

          return {
            ...record,
            ...normalized,
            patientFullName,
            phone,
            doctorFullName,
            doctorPhone,
          };
        });

        setRecords(recordsWithInfo);
        setTotalPages(totalPagesData);
        setLoading(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Erreur inconnue lors du chargement des dossiers médicaux";
        setError(errorMessage);
        setLoading(false);
        console.error("Error fetching medical records:", err);
      }
    };
    fetchRecords();
  }, [currentPage, recordsPerPage, debouncedSearchTerm, refreshTrigger]);

  // Auto-refresh when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const handleViewRecord = (recordId: number) => {
    navigate(`/admin/dashboard/record/view/${recordId}`);
  };

  const handleUpdateRecord = (recordId: number) => {
    navigate(`/admin/dashboard/record/edit/${recordId}`);
  };

  const filteredRecords = records;

  // Since we are using server-side pagination, the records we have are already for the current page.
  // We don't need to slice them again based on page number.
  const currentRecords = filteredRecords;

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  if (loading)
    return (
      <LoadingScreen
        message="Chargement des dossiers médicaux..."
        role="admin"
      />
    );
  if (error) return <ResponseMessage message={error} role="admin" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Dossiers Médicaux"
        subtitle="Gérez vos dossiers médicaux."
        icon={<Plus className="w-6 h-6 text-white" />}
        role="admin"
        actionLink="/admin/dashboard/record/new"
        actionText="Nouveau Dossier"
        description=""
      />
      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-slideInUp">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-grow">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Rechercher par patient ou téléphone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                aria-label="Rechercher des dossiers médicaux"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { label: "Patient", class: "" },
                    { label: "Téléphone", class: "" },
                    { label: "Date", class: "" },
                    { label: "Médecin", class: "" },
                    { label: "Diagnostic", class: "" },
                    { label: "Impayé", class: "" },
                    { label: "Actions", class: "text-center" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.length > 0 ? (
                  currentRecords.map((record) => (
                    <tr
                      key={record.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {record.patientFullName || "Nom inconnu"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {record.phone || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {record.dateCreation
                          ? new Date(record.dateCreation).toLocaleDateString(
                            "fr-FR"
                          )
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {record.doctorFullName || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 max-w-xs truncate">
                        {record.diagnostic || record.assessment || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-semibold text-red-600">
                        {typeof record.amountDue === "number"
                          ? `${record.amountDue} MRU`
                          : `${Math.max(
                            0,
                            (record.consultationFee || 0) -
                            (record.amountPaid || 0)
                          )} MRU`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() => handleViewRecord(record.id)}
                            aria-label="Voir le dossier"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-500 hover:bg-yellow-100 hover:text-yellow-700 transition-colors"
                            onClick={() => handleUpdateRecord(record.id)}
                            aria-label="Modifier le dossier"
                          >
                            <Pencil size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      {searchTerm ?
                        `Aucun résultat trouvé pour "${searchTerm}"` :
                        "Aucun dossier médical"
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage des dossiers {(currentPage - 1) * recordsPerPage + 1} à{" "}
              {Math.min(currentPage * recordsPerPage, (currentPage - 1) * recordsPerPage + filteredRecords.length)} sur{" "}
              {totalPages * recordsPerPage} (Total estimé)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  totalPages <= 5
                    ? i + 1
                    : currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordForm;
